import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  BookOpen, Save, ChevronLeft, ChevronRight, Loader2,
  Eye, EyeOff, Sparkles, FileText, Award, User, BookMarked,
  AlignLeft, Hash, Globe,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { getProfile, PROFILE_OPTIONS, interpolate } from '../../lib/universityProfiles'
import toast from 'react-hot-toast'
import { generateAnthropic } from '../../lib/anthropic'
import { composeThesisDocument } from '../../utils/exportCompositionEngine'

// ── Section navigation config ─────────────────────────────────────────────────

const SECTIONS = [
  { id: 'variables',          label: 'Thesis Details',      icon: User },
  { id: 'title',              label: 'Title Page',          icon: BookOpen },
  { id: 'certificate_supervisor', label: 'Supervisor Certificate', icon: Award },
  { id: 'declaration',        label: 'Declaration',         icon: FileText },
  { id: 'dedication',         label: 'Dedication',          icon: BookMarked },
  { id: 'acknowledgements',   label: 'Acknowledgements',    icon: AlignLeft },
  { id: 'abstract',           label: 'Abstract',            icon: Hash },
  { id: 'list_of_figures',    label: 'List of Figures',     icon: Eye },
  { id: 'list_of_tables',     label: 'List of Tables',      icon: Eye },
]

// ── Field editor row ──────────────────────────────────────────────────────────

function Field({ label, value, onChange, multiline = false, placeholder = '' }) {
  const base = {
    width: '100%',
    background: '#FFFFFF',
    border: '1px solid #C8D4E4',
    borderRadius: 8,
    fontFamily: 'inherit',
    fontSize: 14,
    color: '#1a2234',
    padding: '0.5rem 0.75rem',
    outline: 'none',
    transition: 'border-color 0.15s',
  }
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label}
      </label>
      {multiline ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          rows={6}
          style={{ ...base, resize: 'vertical', lineHeight: 1.6 }}
          onFocus={e => (e.target.style.borderColor = '#2563eb')}
          onBlur={e => (e.target.style.borderColor = '#C8D4E4')}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={base}
          onFocus={e => (e.target.style.borderColor = '#2563eb')}
          onBlur={e => (e.target.style.borderColor = '#C8D4E4')}
        />
      )}
    </div>
  )
}

// ── Preview renderer ──────────────────────────────────────────────────────────

function PreviewPane({ html }) {
  return (
    <div
      style={{
        flex: 1,
        background: '#f5f0e8',
        borderRadius: 8,
        overflow: 'auto',
        padding: '3rem 3.5rem',
        fontFamily: '"Times New Roman", Georgia, serif',
        fontSize: 12,
        lineHeight: 2,
        color: '#1a1410',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)',
        minHeight: 500,
      }}
      dangerouslySetInnerHTML={{ __html: html || '<p style="color:#94a3b8;font-family:sans-serif;font-size:13px">Preview will appear here</p>' }}
    />
  )
}

// ── Auto-generated figure/table list ─────────────────────────────────────────
//
// Reads from composeThesisDocument — the same scan the real DOCX/PDF export
// runs — instead of the thesis_figures DB table. That table is only ever
// populated by the LLM-generation pipeline (tokenizeVisuals); figures added
// via "Insert Conceptual Figures" / "Insert Research Figures" go straight
// into chapter HTML and never get a row there, so this list silently missed
// them. It also carried the figure_number assigned once at generation time,
// which the export never uses — export always recomputes the number fresh
// from document position — so the two drifted apart after any add/delete/
// reorder. Scanning the same chapters the export scans keeps this preview
// unable to disagree with what actually ships.

function FigureList({ thesisId, type }) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!thesisId) return
    supabase
      .from('chapters')
      .select('id, chapter_number, title, content')
      .eq('thesis_id', thesisId)
      .order('chapter_number', { ascending: true })
      .then(({ data }) => {
        const composed = composeThesisDocument({ thesis: {}, chapters: data || [] })
        setRows(type === 'figures' ? composed.lof : composed.lot)
        setLoading(false)
      })
  }, [thesisId, type])

  if (loading) return <div style={{ color: '#94a3b8', fontSize: 13, padding: '2rem 0' }}>Loading…</div>
  if (rows.length === 0) return (
    <div style={{ color: '#94a3b8', fontSize: 13, padding: '2rem 0' }}>
      No {type} generated yet. Generate chapter content first.
    </div>
  )

  return (
    <div style={{ fontFamily: '"Times New Roman", Georgia, serif', fontSize: 13 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {rows.map((fig, i) => (
            <tr key={i}>
              <td style={{ paddingBottom: '0.5rem', paddingRight: '1rem', whiteSpace: 'nowrap', color: '#475569', fontSize: 12 }}>
                {fig.displayLabel}
              </td>
              <td style={{ paddingBottom: '0.5rem', flex: 1, overflow: 'hidden' }}>
                {fig.title}
              </td>
              <td style={{ paddingBottom: '0.5rem', paddingLeft: '1rem', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>
                {'...'}
              </td>
              <td style={{ paddingBottom: '0.5rem', paddingLeft: '0.5rem', color: '#64748b', fontSize: 12 }}>
                {/* page number would go here */}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const EMPTY_FM = {
  university_profile:     'kennedy',
  student_name:           '',
  reg_number:             '',
  degree:                 'Doctor of Philosophy',
  specialization:         '',
  supervisor_name:        '',
  supervisor_designation: '',
  supervisor_institution: '',
  co_supervisor_name:     '',
  university_name:        '',
  faculty_name:           '',
  submission_month:       '',
  submission_year:        '',
  declaration_city:       '',
  dedication_text:        '',
  acknowledgement_text:   '',
  abstract_text:          '',
  keywords:               [],
  examiner_name:          '',
  examiner_designation:   '',
}

export default function FrontMatterPage() {
  const { id: thesisId } = useParams()
  const [thesis,  setThesis]  = useState(null)
  const [fm,      setFm]      = useState(EMPTY_FM)
  const [fmId,    setFmId]    = useState(null)
  const [section, setSection] = useState('variables')
  const [saving,  setSaving]  = useState(false)
  const [preview, setPreview] = useState(true)
  const [genLoading, setGenLoading] = useState(false)

  // Load thesis + front matter
  useEffect(() => {
    if (!thesisId) return
    const load = async () => {
      const [{ data: t }, { data: f }] = await Promise.all([
        supabase.from('theses').select('*').eq('id', thesisId).maybeSingle(),
        supabase.from('thesis_front_matter').select('*').eq('thesis_id', thesisId).maybeSingle(),
      ])
      if (t) {
        setThesis(t)
        // Pre-populate from thesis fields
        setFm(prev => ({
          ...prev,
          university_name: t.university || prev.university_name,
          specialization:  t.programme  || prev.specialization,
          ...(f || {}),
        }))
      }
      if (f) { setFmId(f.id) }
    }
    load()
  }, [thesisId])

  const set = (key) => (val) => setFm(prev => ({ ...prev, [key]: val }))

  const save = useCallback(async () => {
    if (!thesisId) return
    setSaving(true)
    try {
      const payload = { ...fm, thesis_id: thesisId, updated_at: new Date().toISOString() }
      if (fmId) {
        await supabase.from('thesis_front_matter').update(payload).eq('id', fmId)
      } else {
        const { data } = await supabase.from('thesis_front_matter').insert(payload).select().single()
        if (data) setFmId(data.id)
      }
      toast.success('Front matter saved')
    } catch (err) {
      toast.error('Save failed: ' + err.message)
    } finally {
      setSaving(false)
    }
  }, [fm, fmId, thesisId])

  // Generate acknowledgements / abstract via AI
  const generateSection = async (sectionKey) => {
    setGenLoading(true)
    try {
      const prompt = sectionKey === 'acknowledgement_text'
        ? `Write a professional doctoral thesis acknowledgements section (200–300 words) for ${fm.student_name || 'the candidate'} pursuing a ${fm.degree} in ${fm.specialization || 'the field'} at ${fm.university_name || 'the university'}. The supervisor is ${fm.supervisor_name || 'the supervisor'}. Academic, sincere, and appropriately formal. Begin directly with the text.`
        : `Write a doctoral thesis abstract for a thesis titled "${thesis?.title || ''}" in the field of ${fm.specialization || 'the field'} using ${thesis?.methodology || 'mixed methods'} methodology.

STRICT FORMAT — follow this exact four-part structure with no subheadings:
1. PROBLEM (1–2 sentences): State the specific problem and why existing knowledge is insufficient.
2. METHOD (2–3 sentences): Describe the research design, sample/data source, and analytical approach.
3. FINDINGS (2–3 sentences): State the principal findings as concrete, specific results — no vague generalisations.
4. CONTRIBUTION (1–2 sentences): State what this thesis adds to knowledge or practice that was not there before.

WORD COUNT: 150–250 words total. No filler. Every sentence must carry information.
PROHIBITED PHRASES: "this study aims to", "in order to", "it is important to note", "furthermore", "moreover", "this research seeks to", "the findings of this study", "in conclusion".
Begin directly with the abstract text.`

      const text = await generateAnthropic({
        feature:   'abstract',
        maxTokens: 600,
        messages:  [{ role: 'user', content: prompt }],
      })
      if (text) {
        set(sectionKey)(text)
        toast.success('Generated — review and save')
      }
    } catch (err) {
      // Show the real reason — the proxy returns rate-limit and daily-budget
      // messages the user can act on, and "Generation failed" hides them.
      toast.error(err.message || 'Generation failed')
    } finally {
      setGenLoading(false)
    }
  }

  // Build variables for template interpolation
  const vars = {
    thesis_title:           thesis?.title || '[Thesis Title]',
    student_name:           fm.student_name           || '[Student Name]',
    reg_number:             fm.reg_number             || '[Reg. No.]',
    degree:                 fm.degree                 || 'Doctor of Philosophy',
    specialization:         fm.specialization         || '[Specialization]',
    supervisor_name:        fm.supervisor_name        || '[Supervisor Name]',
    supervisor_designation: fm.supervisor_designation || '[Designation]',
    supervisor_institution: fm.supervisor_institution || '[Institution]',
    co_supervisor_name:     fm.co_supervisor_name     || '',
    examiner_name:          fm.examiner_name          || '[Examiner Name]',
    examiner_designation:   fm.examiner_designation   || '[Designation]',
    university_name:        fm.university_name        || '[University Name]',
    faculty_name:           fm.faculty_name           || '[Faculty/Department]',
    submission_month:       fm.submission_month       || '[Month]',
    submission_year:        fm.submission_year        || '[Year]',
    declaration_city:       fm.declaration_city       || '[City]',
    dedication_text:        fm.dedication_text        || '[Dedication text]',
    acknowledgement_text:   fm.acknowledgement_text
      ? `<p>${fm.acknowledgement_text.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`
      : '<p>[Acknowledgements text]</p>',
    abstract_text:          fm.abstract_text
      ? `<p>${fm.abstract_text.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`
      : '<p>[Abstract text]</p>',
    keywords_block:         fm.keywords?.length
      ? `<p style="margin-top:1rem"><strong>Keywords:</strong> ${fm.keywords.join(', ')}</p>`
      : '',
  }

  const profile   = getProfile(fm.university_profile)
  const templates = profile.templates

  // Determine preview HTML for current section
  const previewHtml = () => {
    if (section === 'variables') return ''
    if (section === 'list_of_figures') return '<p style="font-weight:700;text-align:center;margin-bottom:1.5rem">LIST OF FIGURES</p><p style="color:#94a3b8;font-size:0.85rem">[Auto-generated from chapter figures — see table below]</p>'
    if (section === 'list_of_tables')  return '<p style="font-weight:700;text-align:center;margin-bottom:1.5rem">LIST OF TABLES</p><p style="color:#94a3b8;font-size:0.85rem">[Auto-generated from chapter tables — see table below]</p>'
    const tpl = templates[section]
    if (!tpl) return '<p style="color:#94a3b8">No template for this section</p>'
    return interpolate(tpl, vars)
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 4rem)', overflow: 'hidden', background: '#f8fafc' }}>

      {/* ── Left nav ── */}
      <div style={{ width: 220, flexShrink: 0, background: '#FFFFFF', borderRight: '1px solid #DDE4EF', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '1rem 1rem 0.75rem', borderBottom: '1px solid #DDE4EF' }}>
          <Link
            to={`/app/thesis/${thesisId}/chapters`}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#506280', textDecoration: 'none' }}
          >
            <ChevronLeft style={{ width: 12, height: 12 }} />
            Chapters
          </Link>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 12 }}>
            Front Matter
          </p>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem' }}>
          {SECTIONS.map(s => {
            const Icon = s.icon
            const active = section === s.id
            return (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                  padding: '0.5rem 0.75rem', borderRadius: 7, border: 'none', cursor: 'pointer',
                  background: active ? 'rgba(37,99,235,0.08)' : 'transparent',
                  color: active ? '#2563eb' : '#506280',
                  fontSize: 13, fontWeight: active ? 600 : 400, textAlign: 'left',
                  marginBottom: 2,
                }}
              >
                <Icon style={{ width: 14, height: 14, flexShrink: 0 }} />
                {s.label}
              </button>
            )
          })}
        </div>

        {/* Save */}
        <div style={{ padding: '0.75rem', borderTop: '1px solid #DDE4EF' }}>
          <button
            onClick={save}
            disabled={saving}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '0.5rem', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600,
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <Save style={{ width: 14, height: 14 }} />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Top bar */}
        <div style={{ background: '#FFFFFF', borderBottom: '1px solid #DDE4EF', padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h1 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>
              {SECTIONS.find(s => s.id === section)?.label || 'Front Matter'}
            </h1>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '2px 0 0' }}>
              {thesis?.title || ''}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Profile selector */}
            <select
              value={fm.university_profile}
              onChange={e => set('university_profile')(e.target.value)}
              style={{ fontSize: 12, padding: '0.3rem 0.5rem', borderRadius: 6, border: '1px solid #DDE4EF', background: '#f8fafc', color: '#475569', cursor: 'pointer' }}
            >
              {PROFILE_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {/* Preview toggle */}
            <button
              onClick={() => setPreview(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '0.35rem 0.75rem', borderRadius: 7, border: '1px solid #DDE4EF', background: preview ? 'rgba(37,99,235,0.06)' : '#fff', color: preview ? '#2563eb' : '#475569', fontSize: 12, cursor: 'pointer' }}
            >
              {preview ? <EyeOff style={{ width: 13, height: 13 }} /> : <Eye style={{ width: 13, height: 13 }} />}
              {preview ? 'Hide Preview' : 'Show Preview'}
            </button>
          </div>
        </div>

        {/* Content area */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', gap: 0 }}>

          {/* Form panel */}
          <div style={{ width: preview ? 360 : '100%', flexShrink: 0, overflowY: 'auto', padding: '1.5rem', background: '#f8fafc', borderRight: preview ? '1px solid #DDE4EF' : 'none' }}>
            {section === 'variables' && (
              <div>
                <SectionHeader title="Author" />
                <Field label="Student Full Name" value={fm.student_name} onChange={set('student_name')} placeholder="Dr. Jane Smith" />
                <Field label="Registration Number" value={fm.reg_number} onChange={set('reg_number')} placeholder="KU/DBA/2021/001" />
                <Field label="Degree" value={fm.degree} onChange={set('degree')} placeholder="Doctor of Philosophy" />
                <Field label="Specialization / Programme" value={fm.specialization} onChange={set('specialization')} placeholder="Business Administration" />

                <SectionHeader title="Supervision" />
                <Field label="Supervisor Full Name" value={fm.supervisor_name} onChange={set('supervisor_name')} placeholder="Prof. John Doe" />
                <Field label="Supervisor Designation" value={fm.supervisor_designation} onChange={set('supervisor_designation')} placeholder="Professor of Management" />
                <Field label="Supervisor Institution" value={fm.supervisor_institution} onChange={set('supervisor_institution')} placeholder="Kennedy University" />
                <Field label="Co-Supervisor (optional)" value={fm.co_supervisor_name} onChange={set('co_supervisor_name')} placeholder="Dr. Jane Williams" />

                <SectionHeader title="Institution" />
                <Field label="University Name" value={fm.university_name} onChange={set('university_name')} placeholder="Kennedy University" />
                <Field label="Faculty / Department" value={fm.faculty_name} onChange={set('faculty_name')} placeholder="Faculty of Business" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1rem' }}>
                  <Field label="Submission Month" value={fm.submission_month} onChange={set('submission_month')} placeholder="May" />
                  <Field label="Submission Year" value={fm.submission_year} onChange={set('submission_year')} placeholder="2025" />
                </div>
                <Field label="Declaration City" value={fm.declaration_city} onChange={set('declaration_city')} placeholder="New Delhi" />

                <SectionHeader title="Examiner (post-defence)" />
                <Field label="Examiner Name" value={fm.examiner_name} onChange={set('examiner_name')} placeholder="Prof. R. Kumar" />
                <Field label="Examiner Designation" value={fm.examiner_designation} onChange={set('examiner_designation')} placeholder="Professor" />
              </div>
            )}

            {section === 'dedication' && (
              <div>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: '1.25rem' }}>A brief dedication (1–3 lines). Will appear centred on the page.</p>
                <Field label="Dedication Text" value={fm.dedication_text} onChange={set('dedication_text')} multiline placeholder="To my family, whose unwavering support made this journey possible…" />
              </div>
            )}

            {section === 'acknowledgements' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                  <GenBtn onClick={() => generateSection('acknowledgement_text')} loading={genLoading} />
                </div>
                <Field label="Acknowledgements" value={fm.acknowledgement_text} onChange={set('acknowledgement_text')} multiline placeholder="Write your acknowledgements here…" />
              </div>
            )}

            {section === 'abstract' && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                  <GenBtn onClick={() => generateSection('abstract_text')} loading={genLoading} />
                </div>
                <Field label="Abstract (250–300 words)" value={fm.abstract_text} onChange={set('abstract_text')} multiline placeholder="Write your abstract here…" />
                <Field label="Keywords (comma-separated)" value={(fm.keywords || []).join(', ')} onChange={v => set('keywords')(v.split(',').map(k => k.trim()).filter(Boolean))} placeholder="digital leadership, organizational transformation…" />
              </div>
            )}

            {(section === 'title' || section === 'certificate_supervisor' || section === 'certificate_examiner' || section === 'declaration') && (
              <div>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: '1rem' }}>
                  This section uses the template from the selected university profile.<br />
                  Fill in the <strong>Thesis Details</strong> section to populate all fields.
                </p>
                <div style={{ padding: '0.875rem', background: 'rgba(37,99,235,0.06)', borderRadius: 8, border: '1px solid rgba(37,99,235,0.15)', fontSize: 12, color: '#3b82f6' }}>
                  Profile: <strong>{profile.name}</strong> · Citation: {profile.citation}
                </div>
              </div>
            )}

            {(section === 'list_of_figures' || section === 'list_of_tables') && (
              <div>
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: '1rem' }}>
                  Auto-generated from chapter figures. Generate chapters first.
                </p>
                <FigureList thesisId={thesisId} type={section === 'list_of_figures' ? 'figures' : 'tables'} />
              </div>
            )}
          </div>

          {/* Preview panel */}
          {preview && section !== 'variables' && section !== 'list_of_figures' && section !== 'list_of_tables' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '2rem' }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '1rem' }}>
                Preview — {profile.name} Format
              </p>
              <div style={{ maxWidth: 680, margin: '0 auto' }}>
                <PreviewPane html={previewHtml()} />
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ title }) {
  return (
    <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem', marginTop: '1.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid #e2e8f0' }}>
      {title}
    </p>
  )
}

function GenBtn({ onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      style={{
        display: 'flex', alignItems: 'center', gap: 5, padding: '0.4rem 0.75rem',
        borderRadius: 7, border: '1px solid rgba(37,99,235,0.3)',
        background: 'rgba(37,99,235,0.07)', color: '#2563eb', fontSize: 12, cursor: 'pointer',
        opacity: loading ? 0.6 : 1,
      }}
    >
      {loading
        ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} />
        : <Sparkles style={{ width: 12, height: 12 }} />}
      Generate with AI
    </button>
  )
}
